import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { AvailabilityTypes } from 'common';
import { BookingWizardFlowTypes } from 'common/dist/infrastructure/modules/appointment/interfaces/AppointmentTypes';
import { add, format, isSameDay } from 'date-fns';
import jwtDecode from 'jwt-decode';
import { useEffect, useMemo, useState } from 'react';

import { formatPricing } from 'application/modules/bookingWizard/common/helpers';
import {
  AppointmentError,
  AppointmentPricingError,
  ChooseATimeError,
  ChooseATimeForm,
  IChooseATimeAdapter,
} from 'application/modules/bookingWizard/useCases/hooks/useCaseChooseATimeForLesson';
import mboAPI from 'infrastructure/redux/adapters/mboAPI/mboApi';
import { selectAccessToken } from 'infrastructure/redux/slices/auth.selector';
import {
  selectExperienceType,
  selectInstructorDetails,
  selectLocation,
  selectPartySize,
} from 'infrastructure/redux/slices/bookingWizard.selector';
import {
  setAddons,
  setAppointments,
  setReservationDate,
  setResourceId,
  setStartTimeAndSessionLength,
} from 'infrastructure/redux/slices/bookingWizard.slice';
import { setTotalAmountToPay } from 'infrastructure/redux/slices/payment.slice';
import { selectUserSummary } from 'infrastructure/redux/slices/user.selector';
import { setUserSummary } from 'infrastructure/redux/slices/user.slice';
import { useAppDispatch, useAppSelector } from 'infrastructure/redux/store/hooks';

function getError(
  error: FetchBaseQueryError | SerializedError | undefined,
): ChooseATimeError | undefined {
  if (!error) {
    return undefined;
  }
  return ChooseATimeError.Unknown;
}

function getPricingError(
  error: FetchBaseQueryError | SerializedError | undefined,
): AppointmentPricingError | undefined {
  if (!error) {
    return undefined;
  }
  return AppointmentPricingError.Unknown;
}

function getAppointmentError(
  error: FetchBaseQueryError | SerializedError | undefined,
): AppointmentError | undefined {
  if (!error) {
    return undefined;
  }
  return AppointmentError.Unknown;
}

const getEmailFromToken = (token?: string): string | undefined => {
  if (!token) return undefined;
  try {
    return jwtDecode<{ email?: string }>(token).email;
  } catch {
    return undefined;
  }
};

const useChooseATimeHook: IChooseATimeAdapter = () => {
  const dispatch = useAppDispatch();
  const selectedLocationId = useAppSelector(selectLocation);
  const partySize = useAppSelector(selectPartySize);
  const user = useAppSelector(selectUserSummary);
  const accessToken = useAppSelector(selectAccessToken);

  const [startDate, setStartDate] = useState(undefined);
  const [endDate, setEndDate] = useState(undefined);

  const selectedInstructor = useAppSelector(selectInstructorDetails);
  const flowType = useAppSelector(selectExperienceType);
  const userEmail = user?.email || getEmailFromToken(accessToken ?? undefined);

  const {
    data: firstTimeLessonAppointmentAvailabilities,
    isLoading: areFirstTimeLessonLoading,
    error: firstTimeLessonError,
    isFetching: areFirstTimeLessonFetching,
  } = mboAPI.useGetAvailableFirstTimeLessonsAppointmentsQuery(
    {
      locationId: selectedLocationId || '',
      startDateTime: startDate && format(startDate, 'yyyy-MM-dd'),
      endDateTime: endDate && format(endDate, 'yyyy-MM-dd'),
      email: userEmail,
    },
    {
      skip: !selectedLocationId || startDate === undefined || endDate === undefined,
      refetchOnMountOrArgChange: true,
    },
  );

  const {
    data: staffAvailabilities,
    isLoading: areAppointmentsLoading,
    error: appointmentError,
    isFetching: areAppointmentsFetching,
  } = mboAPI.useGetStaffAvailabilityQuery(
    {
      locationId: selectedLocationId || '',
      startDateTime: startDate ? format(startDate, 'yyyy-MM-dd') : '',
      endDateTime: endDate ? format(endDate, 'yyyy-MM-dd') : '',
      staffId: selectedInstructor?.staffId ?? 0,
      email: userEmail,
    },
    {
      skip:
        !selectedLocationId ||
        !selectedInstructor ||
        selectedInstructor.staffId === 0 ||
        startDate === undefined ||
        endDate === undefined,
      refetchOnMountOrArgChange: true,
    },
  );

  // Helper to map and format lesson types
  const mapLessonTypes = (services?: AvailabilityTypes.Service[]) =>
    services
      ?.filter((service) => !/Intro/g.test(service.title))
      ?.filter((service) => !/Fitting/g.test(service.title))
      ?.sort((a, b) => a.price - b.price)
      ?.map((service) => {
        const swingEval = service.title.toLocaleLowerCase().includes('swing');
        const groupLesson = service.title.toLocaleLowerCase().includes('group');
        return {
          title: swingEval
            ? 'Swing Evaluation, 1 Hour'
            : groupLesson
            ? `Group Lesson, ${service.defaultTimeLength / 60} Hour`
            : `Lesson, ${service.title.trim().split(' ').slice(0, 2).join(' ')}`,
          label: !/Group/g.test(service?.title || '') ? '1 Person' : '2-3 People',
          additionalValue: service.defaultTimeLength,
          value: service.sessionTypeId,
          price: service.price,
        };
      });

  // these are lesson types based on real-time availability of instructors
  const availableLessonTypes = mapLessonTypes(
    staffAvailabilities?.find((appt) => appt.staffId === selectedInstructor?.staffId)?.services,
  );

  // these are the lesson types assigned to a specific instructor and are not based on real-time availability
  const scheduledLessonTypes = mapLessonTypes(selectedInstructor?.services);

  const [
    SelectClubFittingType,
    {
      data: clubFittingTypes,
      isLoading: areClubFittingTypesLoading,
      error: clubFittingTypesError,
      isFetching: areClubFittingTypesFetching,
    },
  ] = mboAPI.useLazyGetClubFittingTypesQuery();

  useEffect(() => {
    if (selectedLocationId) {
      SelectClubFittingType(selectedLocationId);
    }
  }, [selectedLocationId]);

  const availableFirstTimeLessonSlots = useMemo(
    () =>
      firstTimeLessonAppointmentAvailabilities?.map((item) => {
        const availabilities = item.availabilities;
        const time = item.time.toString();
        return {
          value: time,
          title: format(new Date(time.slice(0, -1)), 'hh:mm aa'),
          additionalValue: availabilities[0].staffId,
          isDisabled: !availabilities[0].staffId || availabilities[0].resourceIds.length === 0,
          secondaryValue: item.sessionTypeId,
          tertiaryValue: availabilities[0].resourceIds?.[0],
        };
      }),
    [firstTimeLessonAppointmentAvailabilities],
  );

  const [getPricing, result] = mboAPI.useGetAppointmentPricingMutation();
  const [fetchUserData] = mboAPI.useLazyGetUserDataQuery();

  const getAvailableLessonSlotButtons = (sessionTypeId: number) => {
    const instructor = staffAvailabilities?.find(
      (appt) => appt.staffId === selectedInstructor?.staffId,
    );
    const availabilities = instructor?.services?.find(
      (service: AvailabilityTypes.Service) => service.sessionTypeId === sessionTypeId,
    )?.availabilities;

    const slotButtons = availabilities?.map((availability) => {
      const time = availability?.time.toString();

      return {
        value: time,
        title: format(new Date(time?.slice(0, -1)), 'hh:mm aa'),
        secondaryValue: availability?.resourceId,
      };
    });

    return Array.from(
      new Set(
        slotButtons?.map((slotButton) =>
          availabilities?.find((slot) => slot.time.toString() === slotButton.value),
        ),
      ),
    )?.map((availability) => {
      const time = availability?.time.toString() || '';
      return {
        value: time,
        title: format(new Date(time?.slice(0, -1)), 'hh:mm aa'),
        secondaryValue: availability?.resourceId,
      };
    });
  };

  const checkIfAtLeastOneSlotAvailable = (date: Date | null): boolean => {
    if (!date) return false;
    if (flowType === BookingWizardFlowTypes.FirstLesson) {
      return Boolean(firstTimeLessonAppointmentAvailabilities?.length);
    }

    const instructor = staffAvailabilities?.find(
      (appt) => appt.staffId === selectedInstructor?.staffId,
    );
    const allAvailabilities = (
      instructor?.services?.flatMap(
        (service: AvailabilityTypes.Service) => service.availabilities || [],
      ) || []
    ).filter((availability) => {
      if (availability?.time) {
        const availabilityTimeStr = availability.time.toString();
        const availabilityMonth = parseInt(availabilityTimeStr.slice(5, 7));
        const availabilityDay = parseInt(availabilityTimeStr.slice(8, 10));
        const inputMonth = date.getMonth() + 1; // getMonth() is 0-based
        const inputDay = date.getDate();
        if (availabilityMonth === inputMonth && availabilityDay === inputDay) {
          return true;
        }
      }
      return false;
    });

    const uniqueSlots = new Set(allAvailabilities.map((a) => a.time.toString()));

    return uniqueSlots.size > 0;
  };

  const saveAppointmentToStore = (values: ChooseATimeForm) => {
    const resourceId = Number(values?.resourceId);
    const formattedAppointments = formatPricing(result.data);

    const startDateTime = values?.sessionHour;
    const endDateTime = add(new Date(startDateTime), {
      minutes: Number(values.lessonLength),
    }).toISOString();

    dispatch(
      setStartTimeAndSessionLength({
        startTime: startDateTime,
        endTime: endDateTime,
        sessionLength: Number(values.lessonLength) / 60,
      }),
    );
    dispatch(setResourceId({ resourceId }));
    dispatch(
      setAppointments({
        appointments: formattedAppointments.appointments?.map((value) => ({
          ...value,
          resourceId,
        })),
      }),
    );
    dispatch(setAddons(formattedAppointments.addOn));
    const total = formattedAppointments?.appointments.reduce(
      (total, item) => Number(total) + Number(item.price),
      0,
    );
    dispatch(setTotalAmountToPay({ total }));
  };

  const handleGetPricing = async (values: ChooseATimeForm) => {
    const startDateTime = values?.sessionHour;
    const endDateTime = add(new Date(startDateTime), {
      minutes: Number(values.lessonLength),
    }).toISOString();
    const staffId = values.staffId;
    if (selectedLocationId && staffId) {
      let email = userEmail;

      if (!email) {
        try {
          const userData = await fetchUserData(selectedLocationId).unwrap();
          dispatch(setUserSummary(userData));
          email = userData?.email;
        } catch {
          // proceed with base pricing if user fetch fails
        }
      }

      getPricing({
        email,
        startDateTime: startDateTime,
        endDateTime: endDateTime,
        locationId: selectedLocationId,
        sessionTypeId: values?.sessionTypeId,
        staffIds: [staffId],
        partySize,
      });
    }
  };

  const handleSaveReservationDateToStore = (selectedReservationDate: Date) => {
    dispatch(setReservationDate({ reservationDate: selectedReservationDate }));
  };

  return {
    result: firstTimeLessonAppointmentAvailabilities,
    inProgress: areFirstTimeLessonLoading || areFirstTimeLessonFetching,
    error: getError(firstTimeLessonError),
    pricingError: getPricingError(result.error),
    appointentPricing: result.data,
    isPricingLoading: result.isLoading,
    selectedInstructor,
    areAppointmentsLoading: areAppointmentsLoading || areAppointmentsFetching,
    appointmentError: getAppointmentError(appointmentError),
    lessonTypes: availableLessonTypes,
    scheduledLessonTypes,
    saveAppointmentToStore,
    getPricing: handleGetPricing,
    availableFirstTimeLessonSlots,
    setStaffAvailabilitiesStartDate: setStartDate,
    setStaffAvailabilitiesEndDate: setEndDate,
    getAvailableLessonSlotButtons,
    isFirstTimeLesson: flowType === BookingWizardFlowTypes.FirstLesson,
    userData: user,
    handleSaveReservationDateToStore,
    clubFittingTypes,
    clubFittingTypesInProgress: areClubFittingTypesLoading || areClubFittingTypesFetching,
    clubFittingTypesError,
    selectedBookingType: flowType,
    checkIfAtLeastOneSlotAvailable,
    staffAvailabilities,
  };
};

export default useChooseATimeHook;
